import React, { useState } from "react";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import AllEvents from "./AllEvents";

export default function VistaMes({
  currentDate,
  events,
  searchTerm,
  onDateClick,
  onEventClick,
  isToday,
}) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvents, setModalEvents] = useState([]);
  const [modalDate, setModalDate] = useState("");

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => {
    const day = new Date(y, m, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const prevMonthDays = getDaysInMonth(year, month - 1);

  const calendarDays = [];

  // Padding mes anterior
  for (let i = firstDay - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthDays - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    });
  }

  // Mes actual
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      month: month,
      year: year,
      isCurrentMonth: true,
    });
  }

  // Padding mes siguiente hasta completar la semana visible.
  const remainingDays = (7 - (calendarDays.length % 7)) % 7;
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      day: i,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      isCurrentMonth: false,
    });
  }
  const weekRows = calendarDays.length / 7;

  const getEventsForDay = (d, m, y) => {
    const cellDateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(
      d
    ).padStart(2, "0")}`;
    return events.filter((event) => {
      if (
        searchTerm &&
        !event.titulo.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;

      if (!event.fecha) return false;
      
      if (typeof event.fecha === "string") {
        return event.fecha.startsWith(cellDateStr);
      }
      
      const eventDate = new Date(event.fecha);
      return (
        eventDate.getDate() === d &&
        eventDate.getMonth() === m &&
        eventDate.getFullYear() === y
      );
    });
  };

  const DAYS_OF_WEEK = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];

  const handleOpenModal = (e, dayEvents, d, m, y) => {
    e.stopPropagation();
    setModalEvents(dayEvents);
    setModalDate(`${String(d).padStart(2, '0')}/${String(m + 1).padStart(2, '0')}/${y}`);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const renderEvent = (evt, i) => {
    const eventColor = evt.color || "#1e40af";
    return (
      <Tooltip key={evt.id || `${evt.titulo}-${i}`} title={evt.titulo || ""} arrow enterDelay={350}>
        <Box
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (onEventClick) onEventClick(evt);
          }}
          sx={{
            bgcolor: eventColor,
            color: "common.white",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            px: 0.5,
            py: 0.15,
            borderRadius: "3px",
            fontSize: "0.62rem",
            fontWeight: 600,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
            "&:hover": { filter: "brightness(0.95)" },
          }}
        >
          {evt.titulo}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, height: "100%" }}>
      {/* Cabecera días de la semana */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          borderLeft: "2px solid #27272f",
          borderTop: "2px solid #27272f",
          bgcolor: "background.paper",
        }}
      >
        {DAYS_OF_WEEK.map((day) => (
          <Box
            key={day}
            sx={{
              borderRight: "2px solid #27272f",
              borderBottom: "2px solid #27272f",
              minHeight: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              align="center"
              sx={{ color: "text.primary", fontWeight: 500, textTransform: "uppercase", lineHeight: 1 }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Grid del Calendario */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gridTemplateRows: `repeat(${weekRows}, minmax(0, 1fr))`,
          bgcolor: "background.paper",
          borderLeft: "2px solid #27272f",
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
        }}
      >
        {calendarDays.map((cell, index) => {
          const today = isToday(cell.day, cell.month, cell.year);
          const dayEvents = getEventsForDay(cell.day, cell.month, cell.year);

          return (
            <Box
              key={index}
              onClick={() => onDateClick && onDateClick(new Date(cell.year, cell.month, cell.day))}
              sx={{
                bgcolor: cell.isCurrentMonth ? "background.paper" : "#c4c5cc",
                p: 0.5,
                borderRight: "2px solid #27272f",
                borderBottom: "2px solid #27272f",
                cursor: "pointer",
                transition: "background-color 0.2s",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                overflow: "hidden",
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <Stack alignItems="center" mb={0.5}>
                <Typography
                  variant="body2"
                  sx={{
                    color: today ? "primary.contrastText" : cell.isCurrentMonth ? "text.primary" : "text.secondary",
                    bgcolor: today ? "primary.main" : "transparent",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: today ? "bold" : "normal",
                    fontSize: "0.68rem",
                  }}
                >
                  {cell.day}
                </Typography>
              </Stack>

              <Stack spacing={0.5} sx={{ width: "100%", minWidth: 0, flexGrow: 1, minHeight: 0, overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
                {dayEvents.length > 2 ? (
                  <>
                    {renderEvent(dayEvents[0], 0)}
                    <Box
                      onClick={(e) => handleOpenModal(e, dayEvents, cell.day, cell.month, cell.year)}
                      sx={{
                        fontSize: "0.7rem",
                        color: "primary.main",
                        cursor: "pointer",
                        fontWeight: "bold",
                        textAlign: "left",
                        pl: 0.5,
                        "&:hover": { textDecoration: "underline" }
                      }}
                    >
                      + {dayEvents.length - 1} más
                    </Box>
                  </>
                ) : (
                  dayEvents.map((evt, i) => renderEvent(evt, i))
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* Modal de Todos los Eventos */}
      <AllEvents
        open={modalOpen}
        onClose={handleCloseModal}
        date={modalDate}
        events={modalEvents}
        onEventClick={onEventClick}
      />
    </Box>
  );
}
